import React, {useState} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    AssignmentOutlined,
    CheckCircleOutlineRounded,
    CompareOutlined,
    IntegrationInstructionsOutlined,
    ListAltOutlined,
    SettingsOutlined
} from '@mui/icons-material';
import {styled} from '@mui/material/styles';
import {useNavigate, useParams} from 'react-router-dom';
import {useAuth0} from '@auth0/auth0-react';
import {Button} from '../../../components/buttons/Button';
import Popup from '../../../components/popup';
import CreationFlowBar from '../../../components/CreationFlowBar';
import {getAppConfig, useTrovoConfig} from '../../../utils/config';
import {getBooleanLocalStorageFlag, LOCAL_STORAGE_FLAGS, setBooleanLocalStorageFlag} from '../../../utils/localStorageFlags';
import {AnalysisStarted} from '../../../components/modals/analysis/AnalysisStarted';
import {Error} from '../../../components/Error';
import BannerMessage from '../../AccountDetails/BannerMessage';
import Step0 from './steps/Step0';
import ExpandableTable from './steps/step1/ExpandableTable';
import SelectVariables from './steps/step2';
import SelectComparisons from './steps/step3';
import EditSettings from './steps/step4';
import Review from './steps/step5';

const FormContainer = styled(Box)({
    flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    padding: '1.5% 2%',
    gap: 10
});

interface CreateRnaAnalysisProps {
    experiment: any;
    samples: any[];
}

function CreateRnaAnalysis({experiment, samples}: CreateRnaAnalysisProps) {
    const [error, setError] = useState<any>(null);
    /** These do not stop the user from continuing. */
    const [warning, setWarning] = useState<string>('');
    const [isAnalysisStarted, setAnalysisStarted] = useState(false);
    const [dontShowAnalysisStarted, setDontShowAnalysisStartedState] = useState(() =>
        getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.analysisStartedPopup)
    );
    const [step, setStep] = useState(0);
    const [ableToSave, setAbleToSave] = useState(false);
    const samplesD = samples;
    const {getAccessTokenSilently} = useAuth0();
    const {apiHost} = getAppConfig();
    const {id} = useParams();
    const navigate = useNavigate();
    const [groupLevels, setGroupLevels] = useState([]);
    const {user} = useTrovoConfig();
    const [contrastOptions, setContrastOptions] = useState([]);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        samples: [],
        organism: experiment.organism,
        status: 'processing',
        archived: false,
        experiment_id: id,
        group_options: [],
        selected_group_options: {},
        selected_contrast_options: {},
        analyze_config: {
            normalization_method: 'rld',
            filter_source: 'mean_count',
            filter_threshold: 5,
            DE_padjust_method: 'BH',
            lfc_shrinkage: false,
            GO_padjust_method: 'fdr',
            pvalue_option: 'padj',
            pvalue_threshold: 0.05,
            fold_change_threshold: 2,
            if_separate_regulation: false,
            ontology_pvalue_cutoff: 0.05
        }
    });

    console.log('formData', formData);
    console.log('samplesD', samplesD);

    const handleDontShowAnalysisStartedChange = (checked: boolean) => {
        setDontShowAnalysisStartedState(checked);
        setBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.analysisStartedPopup, checked);
    };

    interface RequestBody {
        sample_ids: string[];
        levels: {
            [key: string]: string[];
        };
    }

    /**
     * Formats the request body for the contrast-options BFF endpoint.
     *
     * Checks the selected group_options keys for errors. Uses outer scope
     * `setError` if any.
     *
     * @returns RequestBody if no errors, undefined otherwise
     */
    function formatContrastRequestBody(formData: any, groupLevels: any): RequestBody | undefined {
        let requestBody: RequestBody = {
            sample_ids: formData.samples,
            levels: {}
        };

        function findKeyIgnoreCase(key: string, obj: any): string | undefined {
            const lowerCaseKey = key?.toLowerCase();
            return Object.keys(obj).find(currentKey => currentKey.toLowerCase() === lowerCaseKey);
        }

        for (let option of formData.group_options) {
            let normalizedKey = option.name.toLowerCase().replace(/ /g, '_');
            let originalKey = findKeyIgnoreCase(normalizedKey, groupLevels);
            if (!originalKey) {
                setError(`"${normalizedKey}" is not a valid option. Please choose a different option.`);
                return;
            }
            let values: any[] = [];
            values.push(option.value);
            let correspondingValues = groupLevels[originalKey] || [];
            for (const value of correspondingValues) {
                if (!values.includes(value.toString())) {
                    values.push(value.toString());
                }
            }
            requestBody.levels[originalKey] = values;
        }

        setFormData({...formData, selected_group_options: requestBody.levels});
        return requestBody;
    }

    const getComparisons = async () => {
        const endpoint = `${apiHost}/resources/contrast-options`;
        const method = 'POST';
        const body = formatContrastRequestBody(formData, groupLevels);

        // If body is undefined, an error was set in formatContrastRequestBody.
        if (body === undefined) return;

        const response = await fetch(endpoint, {
            method: method,
            headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        const responseData = await response.json();

        if (responseData?.Error) {
            if (typeof responseData.Error === 'string') {
                setError('Error: ' + responseData.Error);
            } else {
                // TODO: I don't think this is actually ever not a string
                // unless the BFF messes up somehow, but it was here to begin
                // with, so keeping it.
                setError('Error: ' + Object.entries(responseData.Error).join(', '));
            }
            return;
        }

        try {
            // Bff incorrectly returns a stringified JSON object until BFF MR 265 is merged.
            const parsed = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;
            if (parsed?.options) {
                setContrastOptions(parsed.options);
                setError(null);
                setStep(step + 1);
                // Join is empty string if array empty
                setWarning(parsed.message.join('; '));
            } else {
                setError('Unexpected response, no contrast options found.');
            }
        } catch (e) {
            console.error('Error parsing responseData:', e);
            setError('Error parsing response data. Please try again later.');
        }
    };

    /**
     * Calls group-options BFF endpoint to figure out what group options are
     * available for the selected samples.
     */
    const getGroupOptions = async () => {
        const endpoint = `${apiHost}/resources/group-options`;
        const method = 'POST';
        const body = JSON.stringify({inputs: formData.samples});
        const response = await fetch(endpoint, {
            method: method,
            headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: body
        });
        const responseData = await response.json();
        console.log('getGroupOptions responseData', responseData);

        if (response.status === 200) {
            setGroupLevels(responseData?.levels);
            setStep(2);
            setError(null);
            return;
        }
        if (response.status === 400) {
            return setError(responseData?.Error || responseData);
        }
        if (responseData.Error) {
            setError(responseData.Error);
        }
    };

    const createAnalysis = async () => {
        const {name, description, organism, selected_group_options, status, archived, experiment_id} = formData;
        const endpoint = `${apiHost}/experiment/${id}/analysis/create`;
        const method = 'POST';
        const response = await fetch(endpoint, {
            method: method,
            headers: {
                Authorization: `Bearer ${await getAccessTokenSilently()}`,
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                analysisMetadata: {
                    name,
                    description: description || ' ',
                    organism,
                    group_options: selected_group_options,
                    status,
                    archived,
                    experiment_id,
                    analysis_type: 'RNA Seq'
                },
                sample_ids: formData.samples
            })
        });
        const responseData = await response.json();
        if (response.ok) {
            console.log('createAnalysis responseData', responseData);
            const a_id = responseData.id;
            const endpoint = `${apiHost}/experiment/${id}/analysis/${a_id}/start`;
            const method = 'POST';
            const startResponse = await fetch(endpoint, {
                method: method,
                headers: {
                    Authorization: `Bearer ${await getAccessTokenSilently()}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    selected_group_options: formData.selected_group_options,
                    selected_contrast_options: formData.selected_contrast_options,
                    ...formData.analyze_config
                })
            });
            const startResponseData = await startResponse.json();
            console.log('createAnalysis startResponseData', startResponseData);
            if (startResponse.ok) {
                if (getBooleanLocalStorageFlag(LOCAL_STORAGE_FLAGS.analysisStartedPopup)) {
                    navigate(`/experiment/${id}`);
                } else {
                    // TODO: Only hook when making this a standalone function?
                    setAnalysisStarted(true);
                }
            }
        } else {
            console.error(responseData.message || 'An error occurred.');
        }
    };

    const handleSave = async () => {
        setWarning('');
        step === 0 && setStep(step + 1);
        step === 1 && (await getGroupOptions());
        step === 2 && formData.group_options.length && (await getComparisons());
        step === 3 && setStep(step + 1);
        step === 4 && setStep(step + 1);
        step === 5 && createAnalysis();
    };

    const steps = [
        {
            name: 'Analysis details',
            caption: 'Name your analysis and provide a description',
            description: 'Name your analysis and optionally provide a description.',
            component: (
                <Step0
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    data={samplesD}
                    error={error}
                    setError={setError}
                />
            ),
            icon: <AssignmentOutlined sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select samples',
            caption: 'Indicate which samples you want to include in your analysis.',
            description: 'Indicate which samples should be included for analysis. You can edit samples in Manage Samples.',
            component: (
                <ExpandableTable
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    data={samplesD}
                    error={error}
                    setError={setError}
                />
            ),
            icon: <IntegrationInstructionsOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select variables',
            caption: 'Designate variables to compare in this analysis.',
            description: 'Select up to two variables for comparison in this analysis and indicate each variable’s control value. ',
            component: (
                <SelectVariables
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setAbleToSave}
                    groupLevels={groupLevels}
                    error={error}
                    setError={setError}
                />
            ),
            icon: <ListAltOutlined sx={{fill: step === 2 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Select comparisons',
            caption: 'Choose comparison values to be viewed in this analysis.',
            description:
                'Choose the comparisons you want to have available for visualization. These comparisons determine how you will be able to interact with the visualization of this data set. You can select as many comparisons as you wish. You must select at least one comparison.',
            component: (
                <SelectComparisons
                    formData={formData}
                    setFormData={setFormData}
                    setAbleToSave={setAbleToSave}
                    contrastOptions={contrastOptions}
                    setError={setError}
                />
            ),
            icon: <CompareOutlined sx={{fill: step === 3 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit analysis parameter settings',
            caption: 'Advanced configuration parameters for analysis.',
            description: 'View and manage options for data analysis. ',
            component: <EditSettings formData={formData} setFormData={setFormData} />,
            icon: <SettingsOutlined sx={{fill: step === 4 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Review analysis',
            caption: 'Check your setup before submitting for analysis.',
            description:
                'Confirm your analysis setup. Once an analysis has been started, its settings cannot be changed. If you want to run variations of this analysis, you will need to go through the setup process again.',
            component: (
                <Review
                    formData={formData}
                    setFormData={setFormData}
                    handleNext={setStep}
                    samples={samplesD}
                    contrastOptions={contrastOptions}
                />
            ),
            icon: <CheckCircleOutlineRounded sx={{fill: step === 5 ? 'url(#linearColors)' : ''}} />
        }
    ];

    return (
        <>
            <CreationFlowBar
                title="SETUP NEW ANALYSIS"
                steps={steps}
                step={step}
                setStep={i => {
                    setWarning('');
                    setStep(i);
                }}
            />
            <FormContainer>
                <Box sx={{marginBottom: '40px'}}>
                    <BannerMessage
                        show={user.cb_item_price_id?.includes('demo')}
                        setHide={() => null}
                        showClose={false}
                        title="Upgrade to a paid plan to unlock all features."
                        message="You are currently using a demo account."
                    />
                </Box>
                <Typography variant="headline" size="large">
                    {steps[step].name}
                </Typography>
                <Typography variant="body" size="medium" mb={2}>
                    {steps[step].description}
                </Typography>
                {steps[step].component}
                {error && <Error message={error} />}
                {warning !== '' && <Alert severity="warning">{warning}</Alert>}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!ableToSave || user.cb_item_price_id?.includes('demo')}>
                    {step !== 5 ? 'Continue' : 'Run analysis'}
                </Button>
                <Popup
                    isOpen={isAnalysisStarted}
                    onClose={() => setAnalysisStarted(false)}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description">
                    <AnalysisStarted
                        experimentId={id}
                        onClose={() => setAnalysisStarted(false)}
                        dontShowAgain={dontShowAnalysisStarted}
                        onDontShowAgainChange={handleDontShowAnalysisStartedChange}
                    />
                </Popup>
            </FormContainer>
        </>
    );
}

export default CreateRnaAnalysis;
